"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { sendContactForm } from "@/app/actions/contact";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { contactFormSchema } from "@/types/contact";
import { ArrowRightIcon } from "lucide-react";

export default function ContactForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const form = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
      phone: "",
      website: "",
    },
  });

  async function onSubmit(values: z.infer<typeof contactFormSchema>) {
    if (values.website) {
      console.warn("Bot detected!");
      return;
    }

    setIsLoading(true);
    setSubmitStatus({});

    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const result = await sendContactForm(formData);

      if (result?.success) {
        setSubmitStatus({
          success: true,
          message:
            "Tack för ditt meddelande! Vi återkommer så snart som möjligt.",
        });
        form.reset();
      } else {
        setSubmitStatus({
          success: false,
          message:
            "Det gick tyvärr inte att skicka meddelandet. Försök igen senare.",
        });
      }
    } catch (error) {
      console.error("Failed to submit form:", error);
      setSubmitStatus({
        success: false,
        message: "Ett fel uppstod. Försök igen senare.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        suppressHydrationWarning
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem suppressHydrationWarning>
              <FormLabel>Namn</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ditt namn"
                  autoComplete="name"
                  suppressHydrationWarning
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem suppressHydrationWarning>
              <FormLabel>E-post</FormLabel>
              <FormControl>
                <Input
                  placeholder="din@epost.se"
                  autoComplete="email"
                  suppressHydrationWarning
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem suppressHydrationWarning>
              <FormLabel>Telefon</FormLabel>
              <FormControl>
                <Input
                  placeholder="070-123 45 67"
                  autoComplete="tel"
                  suppressHydrationWarning
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Meddelande</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Ditt meddelande"
                  className="resize-none"
                  rows={4}
                  suppressHydrationWarning
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Honeypot field - hidden from real users */}
        <div style={{ display: "none" }}>
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Webbsida</FormLabel>
                <FormControl>
                  <Input {...field} tabIndex={-1} suppressHydrationWarning />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {submitStatus.message && (
          <div
            className={`p-4 rounded-md ${
              submitStatus.success
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {submitStatus.message}
          </div>
        )}

        <Button
          effect="expandIcon"
          icon={ArrowRightIcon}
          iconPlacement="right"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? "Skickar..." : "Skicka"}
        </Button>
      </form>
    </Form>
  );
}
